/**
 * Smoosh Starter Kit Bundler — V2 JS
 *
 *   LAYER 1 — Global cart maintenance (kit-card injection in cart drawer +
 *             /cart page). Self-deduping via window._skLayer1Loaded — safe to
 *             coexist with the original product.starter-kit.js Layer 1.
 *
 *   LAYER 2 — V2 PDP UI (binds to .starter-kit-pdp-v2 only). Independent
 *             sentinel: window._skLayer2v2Loaded.
 *
 * Flow (V2):
 *   - Auto-default to subscribe mode
 *   - Required soap count comes from data-required-soaps (default 2)
 *   - Hovering / clicking a soap row swaps the main image to that scent
 *   - One-time purchase lives inside an expandable <details>; its CTA fires
 *     a one-time add-to-cart with the same bundle pipeline
 *   - Mobile sticky bottom bar mirrors the desktop CTA state
 */

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 1 — Global cart-maintenance (every page)
// ═══════════════════════════════════════════════════════════════════════════════
(function () {
  'use strict';

  if (window._skLayer1Loaded) return;
  window._skLayer1Loaded = true;

  const BUNDLE_KEY_PREFIX = 'smoosh-starter-kit';
  window._skBundleKeyPrefix = BUNDLE_KEY_PREFIX;

  function isBundleItem(item) {
    return item.properties &&
      typeof item.properties['_bundle_key'] === 'string' &&
      item.properties['_bundle_key'].startsWith(BUNDLE_KEY_PREFIX);
  }

  // See product.starter-kit.js for the full explanation: while suppressed, a
  // re-inject request from an unrelated cart add (different page/widget) is
  // silently dropped and nothing else brings the kit card back. Reconcile
  // once whenever suppression is released (normal release + 3s safety timer)
  // by re-running injectKitCard() — idempotent, rebuilds from the live cart.
  let _suppressReInject   = false;
  let _suppressReleaseTimer = null;

  function _setSuppressed(val) {
    const wasSuppressed = _suppressReInject;
    _suppressReInject = val;
    if (_suppressReleaseTimer) clearTimeout(_suppressReleaseTimer);
    if (val) {
      _suppressReleaseTimer = setTimeout(() => {
        _suppressReInject = false;
        injectKitCard();
      }, 3000);
    } else if (wasSuppressed) {
      injectKitCard();
    }
  }
  window._skSetSuppressed = _setSuppressed;

  // Discount codes are CMS-driven (price code + optional free shipping code).
  // We send them as a single comma-separated value to /cart/update.js — Shopify's
  // Cart Ajax API supports multi-code stacking when the codes' Combinations
  // settings allow it. This is the modern stable pattern; sequential calls were
  // historically unreliable (each call replacing the previous code).
  //
  // The free shipping code itself is unconditional in Shopify Admin — the JS is
  // the gate, only ever applying it on subscription kit ATC. Risk if leaked:
  // free shipping (~$8) per fraudulent use, not the price reduction.
  let _discountsApplied = ""; // last applied joined-codes string

  async function _applyDiscount(codeOrCodes) {
    const codes = (Array.isArray(codeOrCodes) ? codeOrCodes : [codeOrCodes])
      .map(c => (c || '').trim())
      .filter(Boolean);
    if (!codes.length) return;
    const joined = codes.join(',');
    if (_discountsApplied === joined) return;
    try {
      const res = await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ discount: joined })
      });
      if (!res.ok) throw new Error('Discount apply failed: ' + res.status);
      _discountsApplied = joined;
    } catch (e) { console.warn('[StarterKit V2] Discount apply error:', e); }
  }

  async function _clearDiscount() {
    _discountsApplied = "";
    try {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ discount: '' })
      });
    } catch (e) { console.warn('[StarterKit V2] Discount clear error:', e); }
  }

  window._skApplyDiscount  = _applyDiscount;
  window._skClearDiscount  = _clearDiscount;

  function injectKitCard() {
    const _cms = (window.StarterKitData && window.StarterKitData.cms) || {};
    const kitTitle    = _cms.kitCardTitle    || 'Smoosh Body Starter Kit';
    const kitSubBadge = _cms.kitCardSubBadge || '↻ Auto-ship';
    const kitRefill   = _cms.kitCardRefill   || '↻ Soaps refill on schedule';
    const kitRemove   = _cms.kitCardRemove   || 'Remove kit';

    const contexts = [];
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      contexts.push({ type: 'drawer', container: drawerEl, rowPrefix: '#CartDrawer-Item-', wrapperSel: '.drawer__cart-items-wrapper' });
    }
    const cartPageEl = document.querySelector('#main-cart-items');
    if (cartPageEl) {
      contexts.push({ type: 'page', container: cartPageEl, rowPrefix: '#CartItem-', wrapperSel: '.js-contents' });
    }
    if (!contexts.length) return;

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const allBundleItems = cart.items.filter(isBundleItem);
        if (!allBundleItems.length) return;

        document.querySelectorAll('.sk-cart-kit-card').forEach(el => el.remove());

        const hasStandalone = cart.items.some(item => !isBundleItem(item));

        const groups = {};
        allBundleItems.forEach(item => {
          const key = item.properties['_bundle_key'];
          if (!groups[key]) groups[key] = [];
          groups[key].push(item);
        });

        contexts.forEach(ctx => {
          const container = ctx.type === 'drawer'
            ? (document.querySelector('cart-drawer') || ctx.container)
            : (document.querySelector('#main-cart-items') || ctx.container);

          allBundleItems.forEach(item => {
            const idx = cart.items.indexOf(item) + 1;
            const row = container.querySelector(`${ctx.rowPrefix}${idx}`);
            if (row) row.setAttribute('data-bundle-key', BUNDLE_KEY_PREFIX);
          });

          const itemsWrapper = container.querySelector(ctx.wrapperSel) || container;
          const cartTable    = itemsWrapper.querySelector('table.cart-items');

          if (cartTable) cartTable.style.display = hasStandalone ? '' : 'none';

          Object.entries(groups).forEach(([bundleKey, bundleItems]) => {
            const scrubber    = bundleItems.find(i => i.properties['_bundle_label'] === 'scrubber');
            const soaps       = bundleItems.filter(i => i.properties['_bundle_label'] === 'soap');
            const isSubscribe = soaps.some(i => i.selling_plan_allocation);

            const scrubberRow = scrubber ? `
              <div class="sk-cart-kit-component">
                <img src="${scrubber.image}" alt="${scrubber.product_title}" class="sk-cart-kit-component__img">
                <span class="sk-cart-kit-component__name">${scrubber.product_title} — ${scrubber.variant_title}</span>
                <span class="sk-cart-kit-component__tag">×1${isSubscribe ? ' · One Time' : ''}</span>
              </div>` : '';

            const soapRows = soaps.map(item => `
              <div class="sk-cart-kit-component">
                <img src="${item.image}" alt="${item.product_title}" class="sk-cart-kit-component__img">
                <span class="sk-cart-kit-component__name">${item.product_title}</span>
                <span class="sk-cart-kit-component__tag">×${item.quantity}</span>
              </div>`).join('');

            const totalCents     = bundleItems.reduce((sum, i) => sum + i.final_line_price, 0);
            const origCents      = bundleItems.reduce((sum, i) => sum + i.original_line_price, 0);
            const totalFormatted = (totalCents / 100).toFixed(2);
            const hasKitDiscount = origCents > totalCents;

            const card = document.createElement('div');
            card.className = 'sk-cart-kit-card';
            card.dataset.bundleKey = bundleKey;
            card.innerHTML = `
              <div class="sk-cart-kit-card__header">
                <div class="sk-cart-kit-card__title-wrap">
                  <span class="sk-cart-kit-card__title">${kitTitle}</span>
                  ${(isSubscribe && hasKitDiscount) ? `<span class="sk-cart-kit-card__sub-badge">${kitSubBadge}</span>` : ''}
                </div>
                <div class="sk-cart-kit-card__price">${hasKitDiscount ? `<s class="sk-cart-kit-card__compare">$${(origCents / 100).toFixed(2)}</s> ` : ''}$${totalFormatted}</div>
              </div>
              <div class="sk-cart-kit-card__components">
                ${scrubberRow}
                ${soapRows}
              </div>
              <div class="sk-cart-kit-card__footer">
                ${isSubscribe ? `<span class="sk-cart-kit-card__refill-note">${kitRefill}</span>` : '<span></span>'}
                <button class="sk-cart-kit-card__remove" type="button">${kitRemove}</button>
              </div>`;

            card.querySelector('.sk-cart-kit-card__remove').addEventListener('click', async () => {
              _setSuppressed(true);
              const updates = {};
              bundleItems.forEach(item => { updates[item.key] = 0; });

              const updatedCart = await fetch('/cart/update.js', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ updates })
              }).then(r => r.json()).catch(() => null);

              if (updatedCart) {
                const skSubscribeRemains = updatedCart.items.some(item =>
                  isBundleItem(item) && item.properties['_bundle_mode'] === 'subscribe'
                );
                if (!skSubscribeRemains) await _clearDiscount();
              }

              if (ctx.type === 'drawer') {
                const freshDrawer = document.querySelector('cart-drawer');
                const sections = await fetch('/cart?sections=cart-drawer,cart-icon-bubble')
                  .then(r => r.json()).catch(() => null);

                if (freshDrawer && sections && typeof freshDrawer.renderContents === 'function') {
                  const isEmpty = updatedCart && updatedCart.item_count === 0;
                  freshDrawer.classList.toggle('is-empty', isEmpty);
                  freshDrawer.renderContents({ sections });
                  if (!isEmpty) {
                    setTimeout(() => {
                      injectKitCard();
                      setTimeout(() => { _setSuppressed(false); }, 1000);
                    }, 250);
                  } else {
                    setTimeout(() => { _setSuppressed(false); }, 1200);
                  }
                } else {
                  _setSuppressed(false);
                }
              } else {
                window.location.reload();
              }
            });

            if (cartTable) {
              if (hasStandalone) cartTable.after(card); else cartTable.before(card);
            } else {
              itemsWrapper.appendChild(card);
            }
          });
        });
      });
  }

  window._skInjectKitCard = function () { injectKitCard(); };

  let _reInjectTimer = null;
  function _scheduleReInject(delay) {
    if (_suppressReInject) return;
    if (_reInjectTimer) clearTimeout(_reInjectTimer);
    _reInjectTimer = setTimeout(() => {
      if (_suppressReInject) return;
      injectKitCard();
    }, delay);
  }

  const obs = new MutationObserver((mutations) => {
    if (_suppressReInject) return;
    const cartDrawer = document.querySelector('cart-drawer');
    const cartPage   = document.querySelector('#main-cart-items');
    const relevant = mutations.some(m => {
      if ([...m.addedNodes, ...m.removedNodes].some(n =>
        n.nodeName === 'CART-DRAWER' || n.nodeName === 'CART-ITEMS')) return true;
      if (cartDrawer && cartDrawer.contains(m.target)) return true;
      if (cartPage && cartPage.contains(m.target)) return true;
      return false;
    });
    if (!relevant) return;
    _scheduleReInject(750);
  });
  obs.observe(document.body, { childList: true, subtree: true });

  function _attachPubSubListener() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => { _scheduleReInject(650); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      _attachPubSubListener();
      if (document.querySelector('#main-cart-items')) injectKitCard();
    });
  } else {
    _attachPubSubListener();
    if (document.querySelector('#main-cart-items')) injectKitCard();
  }
})();


// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 2 (V2) — PDP UI for .starter-kit-pdp-v2
// ═══════════════════════════════════════════════════════════════════════════════
(function () {
  'use strict';

  if (window._skLayer2v2Loaded) return;
  window._skLayer2v2Loaded = true;

  const pdp = document.querySelector('.starter-kit-pdp-v2');
  if (!pdp) return;

  const BUNDLE_KEY_PREFIX = window._skBundleKeyPrefix || 'smoosh-starter-kit';
  const cms               = window.StarterKitData?.cms || {};
  const REQUIRED_SOAPS    = parseInt(pdp.dataset.requiredSoaps, 10) || parseInt(cms.requiredSoaps, 10) || 2;

  // ─── DOM ──
  const colorSwatches  = pdp.querySelectorAll('.sk2-color');
  const colorLabel     = pdp.querySelector('#sk2-color-selected');
  const soapItems      = pdp.querySelectorAll('.sk2-soap');
  const soapCountEl    = pdp.querySelector('#sk2-soap-count');
  const progressBar    = pdp.querySelector('#sk2-progress-bar');
  const mainImage      = pdp.querySelector('#sk2-main-image');
  const mediaReset     = pdp.querySelector('#sk2-media-reset');
  const thumbBtns      = pdp.querySelectorAll('.sk2__thumb');
  const atcBtn         = pdp.querySelector('#sk2-atc-btn');
  const atcLabel       = pdp.querySelector('#sk2-atc-label');
  const atcPrice       = pdp.querySelector('#sk2-atc-price');
  const atcError       = pdp.querySelector('#sk2-atc-error');
  const atcDisclosure  = pdp.querySelector('#sk2-atc-disclosure');
  const onetimeCta     = pdp.querySelector('#sk2-onetime-cta');
  const stickyBar      = pdp.parentElement.querySelector('#sk2-sticky');
  const stickyBtn      = stickyBar?.querySelector('#sk2-sticky-btn');
  const stickyLabel    = stickyBar?.querySelector('#sk2-sticky-label');

  const KIT_IMAGE_DEFAULT = mainImage ? mainImage.src : '';

  // ─── State ──
  const state = {
    scrubberVariantId: null,
    scrubberPrice:     0,
    soapSlots:         new Array(REQUIRED_SOAPS).fill(null)
  };

  const firstColor = pdp.querySelector('.sk2-color.is-selected');
  if (firstColor) {
    state.scrubberVariantId = firstColor.dataset.variantId;
    state.scrubberPrice     = parseInt(firstColor.dataset.variantPrice, 10) || 0;
  }

  // ─── Helpers ──
  const soapCount     = () => state.soapSlots.filter(Boolean).length;
  const countForVid   = (vid) => state.soapSlots.filter(s => s && s.variantId === vid).length;
  const isComplete    = () => !!state.scrubberVariantId && soapCount() >= REQUIRED_SOAPS;
  const hasAnySoap    = () => soapCount() > 0;

  function showError(msg) {
    if (atcError) {
      atcError.textContent = msg;
      atcError.hidden = false;
    } else { console.error('[StarterKit V2]', msg); }
  }
  function clearError() {
    if (atcError) { atcError.textContent = ''; atcError.hidden = true; }
  }

  function incompleteLabel() {
    const remaining = REQUIRED_SOAPS - soapCount();
    const tmpl = cms.atcIncomplete || 'Pick [n] scents to continue';
    if (remaining <= 0) return tmpl.replace('[n]', REQUIRED_SOAPS);
    if (remaining === 1) {
      return tmpl
        .replace('[n]', '1')
        .replace(/scents/i, 'scent');
    }
    return tmpl.replace('[n]', String(remaining));
  }

  // ─── Render ──
  function render() {
    clearError();

    // soap count
    if (soapCountEl) soapCountEl.textContent = `${soapCount()} of ${REQUIRED_SOAPS}`;
    if (progressBar) progressBar.style.width = `${Math.min(100, (soapCount() / REQUIRED_SOAPS) * 100)}%`;

    // color label
    if (colorLabel) {
      const sel = pdp.querySelector('.sk2-color.is-selected');
      colorLabel.textContent = sel ? `— ${sel.dataset.variantTitle}` : '';
    }

    // soap rows
    soapItems.forEach(item => {
      const vid       = item.dataset.variantId;
      const count     = countForVid(vid);
      const addBtn    = item.querySelector('.sk2-soap__add');
      const removeBtn = item.querySelector('.sk2-soap__remove');
      const addText   = item.querySelector('.sk2-soap__add-text');

      item.classList.toggle('is-selected', count > 0);

      if (addText) addText.textContent = count > 0 ? `+${count}` : '+ Add';
      if (removeBtn) removeBtn.hidden = count === 0;

      const atCap = soapCount() >= REQUIRED_SOAPS;
      if (addBtn) addBtn.disabled = atCap || item.classList.contains('is-soldout');
    });

    // CTA
    const complete = isComplete();
    atcBtn.disabled = !complete;
    atcBtn.setAttribute('aria-disabled', String(!complete));
    if (atcLabel && atcPrice) {
      if (complete) {
        atcLabel.textContent = (cms.atcLabel || 'Add to bag').toUpperCase();
        atcPrice.hidden = false;
      } else {
        atcLabel.textContent = incompleteLabel();
        atcPrice.hidden = true;
      }
    }
    if (atcDisclosure) atcDisclosure.hidden = !complete;

    // One-time CTA — always available once at least one bar is selected (so they
    // can experiment), but really requires hitting the cap to be meaningful.
    if (onetimeCta) {
      onetimeCta.disabled = !complete;
    }

    // Sticky mirror
    if (stickyBtn && stickyLabel) {
      stickyBtn.disabled = !complete;
      stickyLabel.textContent = complete ? `${(cms.atcLabel || 'Add to bag').toUpperCase()} · ${cms.offerSale || ''}` : incompleteLabel();
    }
    if (stickyBar) {
      const shouldShow = hasAnySoap() && window.matchMedia('(max-width: 990px)').matches;
      stickyBar.classList.toggle('is-visible', shouldShow);
      stickyBar.hidden = !shouldShow;
      stickyBar.setAttribute('aria-hidden', String(!shouldShow));
    }
  }

  // ─── Color swatch ──
  colorSwatches.forEach(sw => {
    sw.addEventListener('click', (e) => {
      e.preventDefault();
      if (sw.dataset.available === 'false') return;
      colorSwatches.forEach(s => s.classList.remove('is-selected'));
      sw.classList.add('is-selected');
      state.scrubberVariantId = sw.dataset.variantId;
      state.scrubberPrice     = parseInt(sw.dataset.variantPrice, 10) || 0;
      // Color change → return to scrubber image
      if (mainImage && sw.dataset.fullres) {
        mainImage.src = sw.dataset.fullres;
        if (mediaReset) mediaReset.hidden = true;
      }
      render();
    });
  });

  // ─── Soap row interactions ──
  soapItems.forEach(item => {
    const rowBtn    = item.querySelector('.sk2-soap__row');
    const addBtn    = item.querySelector('.sk2-soap__add');
    const removeBtn = item.querySelector('.sk2-soap__remove');

    function getSoapData() {
      return {
        variantId:     item.dataset.variantId,
        productId:     item.dataset.productId,
        sellingPlanId: item.dataset.sellingPlanId || '',
        title:         item.dataset.productTitle,
        price:         parseInt(item.dataset.productPrice, 10) || 0,
        image:         item.dataset.productImage
      };
    }

    function addSoap() {
      if (item.classList.contains('is-soldout')) return;
      if (soapCount() >= REQUIRED_SOAPS) return;
      const idx = state.soapSlots.findIndex(s => s === null);
      if (idx === -1) return;
      state.soapSlots[idx] = getSoapData();
      render();
    }

    function removeSoap() {
      const vid = item.dataset.variantId;
      for (let i = state.soapSlots.length - 1; i >= 0; i--) {
        if (state.soapSlots[i] && state.soapSlots[i].variantId === vid) {
          state.soapSlots[i] = null;
          break;
        }
      }
      // compact (keep nulls at the end)
      const filled = state.soapSlots.filter(Boolean);
      while (filled.length < REQUIRED_SOAPS) filled.push(null);
      state.soapSlots = filled;
      render();
    }

    function showSoapImage() {
      if (!mainImage || !item.dataset.productImage) return;
      mainImage.src = item.dataset.productImage;
      if (mediaReset) mediaReset.hidden = false;
    }

    if (rowBtn) {
      rowBtn.addEventListener('click', (e) => {
        e.preventDefault();
        showSoapImage();
        // briefly highlight this row so the user sees the connection
        soapItems.forEach(i => i.classList.remove('is-active'));
        item.classList.add('is-active');
      });
    }
    if (addBtn)    addBtn.addEventListener('click',    e => { e.stopPropagation(); addSoap(); });
    if (removeBtn) removeBtn.addEventListener('click', e => { e.stopPropagation(); removeSoap(); });
  });

  // ─── Reset image button ──
  if (mediaReset) {
    mediaReset.addEventListener('click', () => {
      const sel = pdp.querySelector('.sk2-color.is-selected');
      if (mainImage) {
        mainImage.src = sel?.dataset.fullres || KIT_IMAGE_DEFAULT;
      }
      mediaReset.hidden = true;
      soapItems.forEach(i => i.classList.remove('is-active'));
    });
  }

  // ─── Thumbnails ──
  thumbBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      thumbBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      if (mainImage && btn.dataset.src) mainImage.src = btn.dataset.src;
      if (mediaReset) mediaReset.hidden = true;
    });
  });

  // ─── ATC handlers (subscribe + one-time) ──
  let _atcBusy = false;

  async function performAdd(mode, sourceBtn) {
    if (_atcBusy) return;
    if (!isComplete()) return;
    _atcBusy = true;

    const { skScrubberVariantMap } = window.StarterKitData || {};
    let skScrubberVid = null;

    if (mode === 'subscribe') {
      const missingSoap = state.soapSlots.find(s => s && !parseInt(s.sellingPlanId, 10));
      if (missingSoap) {
        showError('Subscription setup is incomplete for ' + (missingSoap.title || 'a soap') + '. Please refresh and try again.');
        _atcBusy = false; return;
      }
      const sel = pdp.querySelector('.sk2-color.is-selected');
      const swatchTitle = sel ? sel.dataset.variantTitle : '';
      skScrubberVid = skScrubberVariantMap && skScrubberVariantMap[swatchTitle];
      if (!skScrubberVid) {
        showError('Could not resolve subscription scrubber variant. Please refresh and try again.');
        _atcBusy = false; return;
      }
    }

    const originalLabel = sourceBtn ? sourceBtn.textContent : '';
    if (sourceBtn) {
      sourceBtn.disabled = true;
      sourceBtn.textContent = 'Adding…';
    }

    const bundleKey = `${BUNDLE_KEY_PREFIX}-${Date.now()}`;
    try {
      const items = [];

      if (mode === 'subscribe') {
        items.push({
          id:         skScrubberVid,
          quantity:   1,
          properties: { '_bundle_key': bundleKey, '_bundle_label': 'scrubber', '_bundle_mode': 'subscribe' }
        });
        const soapMap = {};
        state.soapSlots.forEach(s => {
          if (!s) return;
          if (!soapMap[s.variantId]) {
            soapMap[s.variantId] = { qty: 0, sellingPlanId: parseInt(s.sellingPlanId, 10) };
          }
          soapMap[s.variantId].qty += 1;
        });
        Object.entries(soapMap).forEach(([id, { qty, sellingPlanId }]) => {
          items.push({
            id, quantity: qty, selling_plan: sellingPlanId,
            properties: { '_bundle_key': bundleKey, '_bundle_label': 'soap', '_bundle_mode': 'subscribe' }
          });
        });
      } else {
        items.push({
          id:         state.scrubberVariantId,
          quantity:   1,
          properties: { '_bundle_key': bundleKey, '_bundle_label': 'scrubber', '_bundle_mode': 'onetime' }
        });
        const soapQtyMap = {};
        state.soapSlots.forEach(s => {
          if (s) soapQtyMap[s.variantId] = (soapQtyMap[s.variantId] || 0) + 1;
        });
        Object.entries(soapQtyMap).forEach(([id, qty]) => {
          items.push({
            id, quantity: qty,
            properties: { '_bundle_key': bundleKey, '_bundle_label': 'soap', '_bundle_mode': 'onetime' }
          });
        });
      }

      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);

      if (mode === 'subscribe' && window._skApplyDiscount) {
        await window._skApplyDiscount([cms.discountCode, cms.shippingDiscountCode]);
      }

      const sections = await fetch('/cart?sections=cart-drawer,cart-icon-bubble')
        .then(r => r.json()).catch(() => null);

      const drawerEl = document.querySelector('cart-drawer');
      if (drawerEl && typeof drawerEl.renderContents === 'function' && sections) {
        if (window._skSetSuppressed) window._skSetSuppressed(true);
        drawerEl.classList.remove('is-empty');
        drawerEl.renderContents({ sections });
        setTimeout(() => {
          if (window._skInjectKitCard) window._skInjectKitCard();
          setTimeout(() => {
            if (window._skSetSuppressed) window._skSetSuppressed(false);
            _atcBusy = false;
          }, 1000);
        }, 250);
      } else {
        window.location.href = '/cart';
      }

      // Reset selections so they can build another kit
      state.soapSlots = new Array(REQUIRED_SOAPS).fill(null);
      render();
      if (sourceBtn) sourceBtn.textContent = originalLabel;
    } catch (err) {
      console.error('[StarterKit V2] ATC error:', err);
      showError('Something went wrong adding your kit. Please try again.');
      _atcBusy = false;
      if (sourceBtn) {
        sourceBtn.disabled = false;
        sourceBtn.textContent = originalLabel;
      }
      render();
    }
  }

  if (atcBtn && !atcBtn.dataset.skBound) {
    atcBtn.dataset.skBound = '1';
    atcBtn.addEventListener('click', () => performAdd('subscribe', atcBtn));
  }
  if (stickyBtn && !stickyBtn.dataset.skBound) {
    stickyBtn.dataset.skBound = '1';
    stickyBtn.addEventListener('click', () => performAdd('subscribe', stickyBtn));
  }
  if (onetimeCta && !onetimeCta.dataset.skBound) {
    onetimeCta.dataset.skBound = '1';
    onetimeCta.addEventListener('click', () => performAdd('onetime', onetimeCta));
  }

  // Re-render sticky bar visibility on resize
  window.addEventListener('resize', () => render(), { passive: true });

  render();
})();
